import React from 'react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { formatGermanNumber, parseGermanNumberOrNull } from '../../../utils/parseGermanNumber';
import {
  aluminumQuoteFieldLabel,
  copperQuoteFieldLabel,
  fetchCurrentWestmetallQuote,
  type MetalQuoteSource,
} from '../../../utils/metalQuotes';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

const WESTMETALL_MARKET_DATA_URL =
  'https://www.westmetall.com/de/markdaten.php?action=table&field=WM_Cu_high';

type InstanceSettingsResponse = {
  default_surcharge_percent: number;
  copper_quote_eur_per_100kg: number | null;
  aluminum_quote_eur_per_100kg: number | null;
  metal_quote_source: MetalQuoteSource;
  metal_quote_date: string | null;
  metal_procurement_cost_percent: number;
};

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseGermanNumberOrNull(trimmed);
  if (parsed === null || parsed < 0) {
    throw new Error('Notierungen müssen nicht-negative Zahlen sein.');
  }
  return parsed;
}

function formatOptionalNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  return formatGermanNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function MetalQuotesSettingsCard() {
  const [copper, setCopper] = React.useState('');
  const [aluminum, setAluminum] = React.useState('');
  const [source, setSource] = React.useState<MetalQuoteSource>('manual');
  const [quoteDate, setQuoteDate] = React.useState('');
  const [procurementCosts, setProcurementCosts] = React.useState('1');
  const [loading, setLoading] = React.useState(true);
  const [marketLoading, setMarketLoading] = React.useState(false);
  const [marketNote, setMarketNote] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const applySettings = React.useCallback((settings: InstanceSettingsResponse | undefined) => {
    setCopper(formatOptionalNumber(settings?.copper_quote_eur_per_100kg));
    setAluminum(formatOptionalNumber(settings?.aluminum_quote_eur_per_100kg));
    setSource(settings?.metal_quote_source ?? 'manual');
    setQuoteDate(settings?.metal_quote_date ?? '');
    setProcurementCosts(formatOptionalNumber(settings?.metal_procurement_cost_percent) || '1');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/instance-settings')
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Metallnotierungen konnten nicht geladen werden.');
        return data?.settings as InstanceSettingsResponse;
      })
      .then((settings) => {
        if (cancelled) return;
        applySettings(settings);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applySettings]);

  /** Notierungen gelten je Quelle — beim Wechsel nicht die alten Werte weiterschleppen. */
  const changeSource = (next: MetalQuoteSource) => {
    if (next === source) return;
    setSource(next);
    setCopper('');
    setAluminum('');
    setQuoteDate('');
    setMarketNote(null);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/instance-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          copper_quote_eur_per_100kg: parseOptionalNumber(copper),
          aluminum_quote_eur_per_100kg: parseOptionalNumber(aluminum),
          metal_quote_source: source,
          metal_quote_date: quoteDate || null,
          metal_procurement_cost_percent: parseOptionalNumber(procurementCosts) ?? 0,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Metallnotierungen konnten nicht gespeichert werden.');
      applySettings(data?.settings as InstanceSettingsResponse);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const loadCurrentWestmetallQuote = async () => {
    setMarketLoading(true);
    setError(null);
    setMarketNote(null);
    try {
      const suggestion = await fetchCurrentWestmetallQuote();
      setCopper(formatOptionalNumber(suggestion.copperQuoteEurPer100kg));
      setQuoteDate(suggestion.quoteDate ?? '');
      setMarketNote(
        suggestion.note ||
          'Die vorgeschlagene Notierung ist noch nicht gespeichert. Bitte prüfen und speichern.',
      );
      setSaved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMarketLoading(false);
    }
  };

  return (
    <SettingsCard
      title="Notierungen"
      actions={
        <>
          <SettingsCardSavedStatus saved={saved}>Gespeichert.</SettingsCardSavedStatus>
          <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => void save()} disabled={loading || saving}>
            {saving ? <LoadingSpinner size="sm" /> : null}
            Speichern
          </button>
        </>
      }
    >
      {loading ? (
        <InlineLoadingState label="Metallnotierungen werden geladen …" className="justify-start" />
      ) : (
        <div className="space-y-4">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>Notizquelle</span>
              <AppSelect
                value={source}
                onChange={(value) => changeSource(value as MetalQuoteSource)}
                options={[
                  { value: 'manual', label: 'Manuell / andere Quelle' },
                  { value: 'westmetall', label: 'Westmetall WM-Notiz' },
                  { value: 'sonepar', label: 'Sonepar SME / SAN' },
                ]}
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              />
            </label>
            <label className="space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>Notierungsdatum</span>
              <input
                type="date"
                value={quoteDate}
                onChange={(event) => setQuoteDate(event.target.value)}
                className={SETTINGS_INPUT_CLASS}
              />
            </label>
            <label className="space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>Bezugskosten</span>
              <div className="relative">
                <input
                  value={procurementCosts}
                  onChange={(event) => setProcurementCosts(event.target.value)}
                  inputMode="decimal"
                  className={`${SETTINGS_INPUT_CLASS} pr-8 text-right`}
                  placeholder="1"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-app-text-muted">
                  %
                </span>
              </div>
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>{copperQuoteFieldLabel(source)}</span>
              <div className="flex items-center gap-2">
                <input
                  value={copper}
                  onChange={(event) => setCopper(event.target.value)}
                  inputMode="decimal"
                  className={SETTINGS_INPUT_CLASS}
                  placeholder="z. B. 950"
                />
                <span className="shrink-0 text-sm text-app-text-muted">€/100 kg</span>
              </div>
            </label>
            <label className="space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>{aluminumQuoteFieldLabel(source)}</span>
              <div className="flex items-center gap-2">
                <input
                  value={aluminum}
                  onChange={(event) => setAluminum(event.target.value)}
                  inputMode="decimal"
                  className={SETTINGS_INPUT_CLASS}
                  placeholder="z. B. 250"
                />
                <span className="shrink-0 text-sm text-app-text-muted">€/100 kg</span>
              </div>
            </label>
          </div>
          {source === 'westmetall' ? (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={SETTINGS_BTN_OUTLINE}
                onClick={() => void loadCurrentWestmetallQuote()}
                disabled={marketLoading}
              >
                {marketLoading ? <LoadingSpinner size="sm" /> : null}
                Aktuelle WM-Kupfernotiz vorschlagen
              </button>
              <a
                href={WESTMETALL_MARKET_DATA_URL}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-pipedrive-blue hover:underline"
              >
                Westmetall-Notierungen öffnen
              </a>
              <span className="text-xs text-app-text-subtle">
                Wird nur in die Felder übernommen; gespeichert wird erst mit „Speichern“.
              </span>
            </div>
          ) : null}
          {marketNote ? (
            <p className="rounded-md border border-app-border bg-app-surface-muted p-3 text-xs text-app-text-muted">
              {marketNote}
            </p>
          ) : null}
          <p className="text-xs text-app-text-subtle">
            Zuschlag je Mengeneinheit = (Notierung inklusive Bezugskosten − Artikelbasis) / 100 ×
            Metallzahl. Negative Zuschläge werden nicht angesetzt.
          </p>
        </div>
      )}
    </SettingsCard>
  );
}
